import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../services/api';

export type BlockStatus = 'idle' | 'loading' | 'ok' | 'error' | 'hidden';

export interface DashboardBlock<T> {
  status: BlockStatus;
  data: T | null;
  error: string | null;
  retry: () => void;
}

/**
 * Spec 069 AC-A5 — one independent dashboard block. 403 (no screen) and 404 (aggregate not deployed
 * yet) hide the block; any other failure or the 3 s timeout shows "Couldn't load · Retry".
 * `reloadKey` changes (window toggle, page refresh) re-run it; a stale response is dropped.
 */
export function useDashboardBlock<T>(
  fetcher: ((signal: AbortSignal) => Promise<T>) | null,
  reloadKey: string,
): DashboardBlock<T> {
  const [status, setStatus] = useState<BlockStatus>(fetcher ? 'loading' : 'idle');
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const enabled = !!fetcher;

  useEffect(() => {
    const run = fetcherRef.current;
    if (!run) { setStatus('idle'); return; }
    const controller = new AbortController();
    setStatus((s) => (s === 'ok' ? 'ok' : 'loading'));
    setError(null);
    run(controller.signal)
      .then((d) => {
        if (controller.signal.aborted) return;
        setData(d);
        setStatus('ok');
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          setStatus('hidden');
          return;
        }
        setError(err?.name === 'TimeoutError' ? 'Took longer than 3 s' : (err instanceof Error ? err.message : 'Failed'));
        setStatus('error');
      });
    return () => controller.abort();
  }, [enabled, reloadKey, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { status, data, error, retry };
}
